/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable complexity */
import {useInfiniteQuery, type InfiniteData} from '@tanstack/react-query';
import {getProjectTransactions} from '@/services/points';
import {pointsKeys} from '@/hooks/points/queryKeys';
import {queryClient} from '@/utils/queryClient';
import type {ProjectTransactionsData, ProjectTransactionsTab} from '@/services/points/interface';
import type {ProjectId} from '@/types';

/** 被动 invalidate 去重；进入弹窗/tab/项目的新鲜度由显式 refetch 第 1 页保证 */
export const PROJECT_TRANSACTIONS_STALE_TIME = 1000 * 30;

export const PROJECT_TRANSACTIONS_PAGE_SIZE = 20;

const trimInfiniteDataToFirstPage = (
    data: InfiniteData<ProjectTransactionsData> | undefined,
): InfiniteData<ProjectTransactionsData> | undefined => {
    if (!data?.pages.length || data.pages.length <= 1) {
        return data;
    }
    return {
        pages: [data.pages[0]],
        pageParams: [data.pageParams[0] ?? 1],
    };
};

/** 进入/离开某个 tab 时裁到第 1 页，保证 refetch 只打 1 个请求 */
export const trimProjectTransactionsTabToFirstPage = (
    projectId: ProjectId,
    pageSize: number,
    tab: ProjectTransactionsTab,
) => {
    queryClient.setQueryData<InfiniteData<ProjectTransactionsData>>(
        pointsKeys.projectTransactions(projectId, pageSize, tab),
        trimInfiniteDataToFirstPage,
    );
};

/** 关弹窗时裁到第 1 页，避免重开时 infinite refetch 按缓存页数全量请求 */
export const trimProjectTransactionsToFirstPage = (projectId: ProjectId) => {
    queryClient.setQueriesData<InfiniteData<ProjectTransactionsData>>(
        {queryKey: pointsKeys.projectTransactionsPrefix(projectId)},
        trimInfiniteDataToFirstPage,
    );
};

/** 首页模式下可能切换过多个项目，关弹窗时裁剪所有项目流水缓存 */
export const trimAllProjectTransactionsToFirstPage = () => {
    void queryClient.cancelQueries({
        queryKey: [...pointsKeys.all, 'projectTransactions'],
    });
    queryClient.setQueriesData<InfiniteData<ProjectTransactionsData>>(
        {queryKey: [...pointsKeys.all, 'projectTransactions']},
        trimInfiniteDataToFirstPage,
    );
};

export const resetProjectTransactions = (projectId: ProjectId) =>
    queryClient.resetQueries({
        queryKey: pointsKeys.projectTransactionsPrefix(projectId),
    });

export const useProjectTransactions = (
    projectId: ProjectId | undefined,
    pageSize = 20,
    tab: ProjectTransactionsTab = 0,
) => {
    const query = useInfiniteQuery<ProjectTransactionsData>({
        queryKey: pointsKeys.projectTransactions(projectId!, pageSize, tab),
        queryFn: ({pageParam = 1}) =>
            getProjectTransactions({
                projectId: projectId!,
                page: pageParam as number,
                pageSize,
                tab,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const loaded = allPages.length * pageSize;
            return loaded < lastPage.total ? allPages.length + 1 : undefined;
        },
        staleTime: PROJECT_TRANSACTIONS_STALE_TIME,
        refetchOnMount: false,
        enabled: !!projectId,
    });

    return {
        ...query,
        transactions: query.data?.pages.flatMap((p) => p.list) ?? [],
        total: query.data?.pages[0]?.total ?? 0,
        project: query.data?.pages[0]?.project ?? null,
    };
};
